'use client';

import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown, LocateFixed } from 'lucide-react';

interface PropertySearchBarProps {
  value: string;
  onChange: (v: string) => void;
  sortBy: string;
  onSortChange: (v: 'newest' | 'rent-asc' | 'rent-desc' | 'area-desc') => void;
  activeFilterCount: number;
  onToggleFilters: () => void;
  filtersOpen: boolean;
}

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'rent-asc', label: 'Rent: Low to High' },
  { value: 'rent-desc', label: 'Rent: High to Low' },
  { value: 'area-desc', label: 'Largest Area' },
] as const;

export default function PropertySearchBar({
  value,
  onChange,
  sortBy,
  onSortChange,
  activeFilterCount,
  onToggleFilters,
  filtersOpen,
}: PropertySearchBarProps) {
  const [locating, setLocating] = useState(false);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
          );
          const data = await res.json();
          const locality =
            data.address?.suburb ||
            data.address?.neighbourhood ||
            data.address?.village ||
            data.address?.town ||
            data.address?.city_district ||
            data.address?.city ||
            data.address?.county ||
            '';
          if (locality) onChange(locality);
        } catch {
          // silently fail
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
      },
      { timeout: 8000 }
    );
  };

  // Calculate right padding: if value exists show clear btn too
  const inputPrClass = value ? 'pr-16' : 'pr-10';

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* Search Input */}
      <div className="relative flex-1">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search by city, locality, property name, or Listing ID..."
          className={`input-field pl-10 ${inputPrClass} h-11`}
          aria-label="Search properties"
          suppressHydrationWarning
        />

        {/* Use My Location button */}
        <button
          onClick={handleUseMyLocation}
          disabled={locating}
          title="Use my location"
          aria-label="Use my location"
          suppressHydrationWarning
          className={`absolute ${value ? 'right-8' : 'right-3'} top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${
            locating
              ? 'text-primary animate-pulse cursor-not-allowed' :'text-muted-foreground hover:text-primary hover:bg-muted'
          }`}
        >
          <LocateFixed size={15} />
        </button>

        {value && (
          <button
            onClick={() => onChange('')}
            suppressHydrationWarning
            className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className="relative">
        <ChevronDown
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
        />
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value as 'newest' | 'rent-asc' | 'rent-desc' | 'area-desc')}
          className="input-field h-11 pr-8 appearance-none cursor-pointer min-w-[180px]"
          aria-label="Sort properties"
          suppressHydrationWarning
        >
          {sortOptions.map((opt) => (
            <option key={`sort-${opt.value}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Filter Toggle (mobile / tablet) */}
      <button
        onClick={onToggleFilters}
        className={`lg:hidden flex items-center gap-2 h-11 px-4 rounded-xl border font-semibold text-sm transition-all duration-150 ${
          filtersOpen || activeFilterCount > 0
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-card text-foreground border-border hover:bg-muted'
        }`}
        aria-label="Toggle filters"
        aria-expanded={filtersOpen}
        suppressHydrationWarning
      >
        <SlidersHorizontal size={16} />
        Filters
        {activeFilterCount > 0 && (
          <span className="ml-1 bg-white text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
            {activeFilterCount}
          </span>
        )}
      </button>
    </div>
  );
}